import React from 'react';
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import Card from "../../../../components/layouts/Card";
import { Option } from "../../../../interfaces/Options";
import Button from "../../../../components/forms/ButtonEx";
import { RootState } from "../../../../redux/rootReducers";
import { BookingInventory } from "../../../../interfaces/Booking";
import NotificationBar from "../../../../components/NotificationBar";
import { DataTableWrapper } from "../../../../components/data/DataTable";
import { BootstrapGrid } from "../../../../components/layouts/BootstrapGrid";
import { InputWrapper, SelectStyle } from "../../../../components/forms/FormComponents";
import { fieldErrorMessage, fieldHasErrors } from '../../../../helpers/errorHandler';
import { setBooking, setError } from '../../../../redux/reducers/bookings/actions';
import { ErrorResponse } from '../../../../interfaces/Response';

const BookingCreateInventory = () => {
  const dispatch = useDispatch();
  const errors = useSelector((state: RootState) => state.booking.error);
  const isLoading = useSelector((state: RootState) => state.booking.isLoading);
  const booking = useSelector((state: RootState) => state.booking.updatedBooking);
  const inventoryItems = useSelector((state: RootState) => state.inventory.inventories);
  const [burrowedItems, setBurrowedItems] = useState<BookingInventory[]>([]);
  const [inventoryQty, setInventoryQty] = useState<number>(0);
  const [inventoryOptions, setInventoryOptions] = useState<Option[]>([]);
  const [inventorySelected, setInventorySelected] = useState<Option | null>(null);

  useEffect(() => {
    if (booking) {
      const newInventories: BookingInventory[] = [];
      burrowedItems.forEach(inventory => {
        const inventoryId = inventory.id_inventory;
        if (inventoryId) {
          newInventories.push(inventory);
        }
      });
      dispatch(setBooking({
        ...booking,
        inventories: newInventories
      }));
    }
  }, [burrowedItems])

  useEffect(() => {
    const newOptions: Option[] = [];
    inventoryItems.forEach(item => {
      if (item.id) {
        newOptions.push({
          value: item.id?.toString(),
          label: item.item_name
        });
      }
    });
    setInventoryOptions(newOptions);
  }, [inventoryItems]);

  const onChangeSelectedInventory = (e: Option | null) => {
    setInventorySelected(e);
  }

  const onChangeInventoryQty = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    if (!isNaN(val)) {
      setInventoryQty(val);
    }
  }

  const actionAddInventoryItem = () => {
    if (isLoading) return;
    
    const _errors: ErrorResponse = {
      status: 402,
      message: 'Semua kolom isian wajib diisi',
      errors: {}
    }

    if (!inventorySelected) {
      _errors.errors = {
        ..._errors.errors,
        inventory_selector_name: ['Mohon pilih barang yang ingin dipinjam'],
      }
    };
    if (!inventoryQty || isNaN(inventoryQty) || inventoryQty <= 0) {
      _errors.errors = {
        ..._errors.errors,
        inventory_selector_quantity: ['Jumlah barang wajib diisi'],
      }
    }

    if (Object.keys(_errors.errors).length > 0) {
      dispatch(setError(_errors));
      return;
    }

    if (!inventorySelected) return;

    const inventoryId = parseInt(inventorySelected.value);
    const inventoryDetail = inventoryItems.find(i => i.id === parseInt(inventorySelected.value));
    if (!inventoryDetail) return;
    const newOptions = [...burrowedItems];
    const previousItemIndex = burrowedItems.findIndex(i => i.id_inventory === inventoryId)
    if (previousItemIndex >= 0) {
      newOptions[previousItemIndex].quantity = inventoryQty;
    } else {
      newOptions.push({
        quantity: inventoryQty,
        id_inventory: parseInt(inventorySelected.value),
        inventory_detail: inventoryDetail
      });
    }
    setBurrowedItems(newOptions);
  }

  const actionDeleteItem = (index: number) => {
    if (isLoading) return;
    const newOptions = [...burrowedItems];
    newOptions.splice(index, 1);
    setBurrowedItems(newOptions);
  }

  const renderBurrowedItems = () => {
    if (burrowedItems.length > 0)
      return burrowedItems.map((item, index) => (
        <tr key={index}>
          <td>{item.inventory_detail?.item_name}</td>
          <td>{item.quantity}</td>
          <td>
            <Button
              noBlock
              disabled={isLoading}
              isLoading={isLoading}
              onClick={() => actionDeleteItem(index)}
              caption={`Hapus`}
            />
          </td>
        </tr>
      ));
    else 
      return (
        <tr>
          <td colSpan={3} style={{ textAlign: "center" }}>Belum ada barang yang dipinjam.</td>
        </tr>
      )
  }

  return (
    <Card>
      <div className="card--title">
        <h2 className="card--title-title">Tambah Peralatan</h2>
      </div>
      <div className="card--body">
        <NotificationBar type="info" style={{ marginBottom: '10px' }}>
          <React.Fragment>
            <p>Untuk mengubah jumlah barang, Anda dapat memilih nama barang yang ingin Anda ubah pada isian Barang dan memasukkan jumlah barang yang berbeda, kemudian tekan `Tambah / Perbaharui Barang`.</p>
          </React.Fragment>
        </NotificationBar>
        <BootstrapGrid>
          <div className="row">
            <div className="col-lg-4">
              <InputWrapper disabled={isLoading} hasErrors={fieldHasErrors('inventory_selector_name', errors)}>
                <div className="input--field select">
                  <label>Barang</label>
                  <Select
                    styles={SelectStyle}
                    isDisabled={isLoading}
                    value={inventorySelected}
                    options={inventoryOptions}
                    onChange={(e: any) => onChangeSelectedInventory(e)}
                    menuPortalTarget={document.querySelector("body")}
                  />
                </div>
                <div className="input--errors">
                  { fieldErrorMessage('inventory_selector_name', errors) }
                </div>
              </InputWrapper>
              <InputWrapper disabled={isLoading} hasErrors={fieldHasErrors('inventory_selector_quantity', errors)}>
                <div className="input--field">
                  <label>Jumlah Barang</label>
                  <input
                    type="number"
                    disabled={isLoading}
                    value={inventoryQty.toString()}
                    onChange={onChangeInventoryQty}
                    placeholder="Masukkan jumlah barang disini"
                  />
                </div>
                <div className="input--errors">
                  { fieldErrorMessage('inventory_selector_quantity', errors) }
                </div>
              </InputWrapper>

              <Button
                noBlock
                disabled={isLoading}
                isLoading={isLoading}
                onClick={actionAddInventoryItem}
                caption={`Tambah / Perbaharui Barang`}
              />
            </div>
            <div className="col-lg-8">
              <DataTableWrapper style={{ margin: 0 }}>
                <div className="tablewrapper--table">
                  <table>
                    <thead>
                      <tr>
                        <th>Nama</th>
                        <th>Jumlah</th>
                        <th>Tindakan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderBurrowedItems()}
                    </tbody>
                  </table>
                </div>
              </DataTableWrapper>
            </div>
          </div>
        </BootstrapGrid>
      </div>
    </Card>
  )
}

export default BookingCreateInventory;